import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { isStandalonePWA } from '../../helpers/pwa';

const DISMISS_STORAGE_KEY = 'statdog-install-prompt-dismissed-at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const PROMPT_DELAY_MS = 1500;

interface InstallAppPromptProps {
  enabled: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

const wasRecentlyDismissed = () => {
  const dismissedAt = window.localStorage.getItem(DISMISS_STORAGE_KEY);

  if (!dismissedAt) {
    return false;
  }

  const dismissedTime = Number(dismissedAt);
  return Number.isFinite(dismissedTime) && Date.now() - dismissedTime < DISMISS_COOLDOWN_MS;
};

const saveDismissedAt = () => {
  window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
};

const InstallAppPrompt: FC<InstallAppPromptProps> = ({ enabled }) => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setVisible(false);
      saveDismissedAt();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (!enabled || visible || !installPrompt || isStandalonePWA() || wasRecentlyDismissed()) {
      return;
    }

    const timer = window.setTimeout(() => setVisible(true), PROMPT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, installPrompt, visible]);

  const handleInstall = async () => {
    if (!installPrompt) {
      return;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;

    setInstallPrompt(null);
    setVisible(false);

    if (choice.outcome === 'dismissed') {
      saveDismissedAt();
    }
  };

  const handleDismiss = () => {
    saveDismissedAt();
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-100 text-lg font-bold text-green-700">
          SD
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900">Install STATDOG</h2>
          <p className="mt-1 text-sm leading-5 text-gray-600">
            Add STATDOG to your home screen for faster access.
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
        >
          Maybe later
        </button>
        <button
          type="button"
          onClick={handleInstall}
          className="rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800"
        >
          Install
        </button>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
